// src/store/slices/karigar/paymentSlice.js
// ============================================================================
// KARIGAR PAYMENT SLICE - UPDATED VERSION
// ============================================================================
// Changes:
// 1. Added cancelPayment thunk import and handler
// 2. Added loading state for cancel
// 3. Better state structure for payment tracking
// ============================================================================

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPayments,
  fetchPaymentById,
  createPayment,
  updatePayment,
  approvePayment,
  rejectPayment,
  processPayment,
  holdPayment,
  releasePayment,
  cancelPayment, // NEW
  fetchPaymentsByKarigar,
  fetchPaymentsByJob,
  fetchPendingPayments,
  generatePaymentsForPeriod,
  bulkProcessPayments,
  fetchPaymentStats,
  generatePaymentReport,
  fetchKarigarEarnings,
  createAdvancePayment,
  createDeduction,
  calculateJobPayment,
  exportPayments,
} from "../../thunks/karigar/paymentThunks";

const initialState = {
  // List data
  payments: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },

  // Current item
  currentPayment: null,

  // Filters
  filters: {
    status: "",
    karigarId: "",
    jobId: "",
    paymentType: "",
    dateFrom: "",
    dateTo: "",
    branchCode: "",
  },

  // Pending payments
  pendingPayments: [],
  pendingCount: 0,
  pendingTotal: 0,

  // Stats
  stats: null,

  // Report
  report: null,

  // Karigar earnings
  karigarEarnings: null,

  // Job payment calculation
  jobPaymentCalculation: null,

  // Loading states
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    approve: false,
    reject: false,
    process: false,
    hold: false,
    release: false,
    cancel: false, // NEW
    pending: false,
    generate: false,
    bulkProcess: false,
    stats: false,
    report: false,
    earnings: false,
    advance: false,
    deduction: false,
    calculate: false,
    export: false,
  },

  // Messages
  error: null,
  successMessage: null,
};

// Helper to extract data from API response
const extractData = (payload) => {
  if (payload?.data) {
    return payload.data;
  }
  return payload;
};

const paymentSlice = createSlice({
  name: "karigar/payment",
  initialState,
  reducers: {
    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Clear current
    clearCurrentPayment: (state) => {
      state.currentPayment = null;
    },
    clearReport: (state) => {
      state.report = null;
    },
    clearEarnings: (state) => {
      state.karigarEarnings = null;
    },
    clearJobPaymentCalculation: (state) => {
      state.jobPaymentCalculation = null;
    },
    clearPendingPayments: (state) => {
      state.pendingPayments = [];
      state.pendingCount = 0;
      state.pendingTotal = 0;
    },

    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Update payment in list (for optimistic updates)
    updatePaymentInList: (state, action) => {
      const updatedPayment = action.payload;
      if (updatedPayment && updatedPayment._id) {
        const index = state.payments.findIndex(
          (p) => p._id === updatedPayment._id
        );
        if (index !== -1) {
          state.payments[index] = {
            ...state.payments[index],
            ...updatedPayment,
          };
        }
      }
    },

    // Remove payment from pending list
    removeFromPending: (state, action) => {
      const paymentId = action.payload;
      state.pendingPayments = state.pendingPayments.filter(
        (p) => p._id !== paymentId
      );
      state.pendingCount = state.pendingPayments.length;
      state.pendingTotal = state.pendingPayments.reduce(
        (sum, p) => sum + (p.netAmount || 0),
        0
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== FETCH PAYMENTS ==========
      .addCase(fetchPayments.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = extractData(action.payload);
        state.payments = data.payments || data || [];
        if (data.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch payments";
        state.payments = [];
      })

      // ========== FETCH PAYMENT BY ID ==========
      .addCase(fetchPaymentById.pending, (state) => {
        state.loading.detail = true;
        state.error = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.loading.detail = false;
        const data = extractData(action.payload);
        state.currentPayment = data.payment || data || null;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload?.message || "Failed to fetch payment";
      })

      // ========== CREATE PAYMENT ==========
      .addCase(createPayment.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.loading.create = false;
        const data = extractData(action.payload);
        const newPayment = data.payment || data;
        if (newPayment && newPayment._id) {
          state.payments = [newPayment, ...state.payments];
          state.pagination.total += 1;
          // Also add to pending if status is pending
          if (newPayment.status === "pending") {
            state.pendingPayments = [newPayment, ...state.pendingPayments];
            state.pendingCount += 1;
            state.pendingTotal += newPayment.netAmount || 0;
          }
        }
        state.successMessage =
          action.payload?.message || "Payment created successfully";
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload?.message || "Failed to create payment";
      })

      // ========== UPDATE PAYMENT ==========
      .addCase(updatePayment.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        state.loading.update = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
          // Update in pending list too
          const pendingIndex = state.pendingPayments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (pendingIndex !== -1) {
            state.pendingPayments[pendingIndex] = updatedPayment;
            // Recalculate total
            state.pendingTotal = state.pendingPayments.reduce(
              (sum, p) => sum + (p.netAmount || 0),
              0
            );
          }
        }
        state.successMessage =
          action.payload?.message || "Payment updated successfully";
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload?.message || "Failed to update payment";
      })

      // ========== APPROVE PAYMENT ==========
      .addCase(approvePayment.pending, (state) => {
        state.loading.approve = true;
        state.error = null;
      })
      .addCase(approvePayment.fulfilled, (state, action) => {
        state.loading.approve = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
        }
        state.successMessage = action.payload?.message || "Payment approved";
      })
      .addCase(approvePayment.rejected, (state, action) => {
        state.loading.approve = false;
        state.error = action.payload?.message || "Failed to approve payment";
      })

      // ========== REJECT PAYMENT ==========
      .addCase(rejectPayment.pending, (state) => {
        state.loading.reject = true;
        state.error = null;
      })
      .addCase(rejectPayment.fulfilled, (state, action) => {
        state.loading.reject = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
          // Remove from pending (status changed to disputed)
          state.pendingPayments = state.pendingPayments.filter(
            (p) => p._id !== updatedPayment._id
          );
          state.pendingCount = state.pendingPayments.length;
          state.pendingTotal = state.pendingPayments.reduce(
            (sum, p) => sum + (p.netAmount || 0),
            0
          );
        }
        state.successMessage = action.payload?.message || "Payment rejected";
      })
      .addCase(rejectPayment.rejected, (state, action) => {
        state.loading.reject = false;
        state.error = action.payload?.message || "Failed to reject payment";
      })

      // ========== PROCESS PAYMENT ==========
      .addCase(processPayment.pending, (state) => {
        state.loading.process = true;
        state.error = null;
      })
      .addCase(processPayment.fulfilled, (state, action) => {
        state.loading.process = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
          // Remove from pending (status changed to completed)
          state.pendingPayments = state.pendingPayments.filter(
            (p) => p._id !== updatedPayment._id
          );
          state.pendingCount = state.pendingPayments.length;
          state.pendingTotal = state.pendingPayments.reduce(
            (sum, p) => sum + (p.netAmount || 0),
            0
          );
        }
        state.successMessage =
          action.payload?.message || "Payment processed successfully";
      })
      .addCase(processPayment.rejected, (state, action) => {
        state.loading.process = false;
        state.error = action.payload?.message || "Failed to process payment";
      })

      // ========== HOLD PAYMENT ==========
      .addCase(holdPayment.pending, (state) => {
        state.loading.hold = true;
        state.error = null;
      })
      .addCase(holdPayment.fulfilled, (state, action) => {
        state.loading.hold = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
          // Update in pending list
          const pendingIndex = state.pendingPayments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (pendingIndex !== -1) {
            state.pendingPayments[pendingIndex] = updatedPayment;
          }
        }
        state.successMessage = action.payload?.message || "Payment put on hold";
      })
      .addCase(holdPayment.rejected, (state, action) => {
        state.loading.hold = false;
        state.error = action.payload?.message || "Failed to hold payment";
      })

      // ========== RELEASE PAYMENT ==========
      .addCase(releasePayment.pending, (state) => {
        state.loading.release = true;
        state.error = null;
      })
      .addCase(releasePayment.fulfilled, (state, action) => {
        state.loading.release = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
          // Update in pending list
          const pendingIndex = state.pendingPayments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (pendingIndex !== -1) {
            state.pendingPayments[pendingIndex] = updatedPayment;
          }
        }
        state.successMessage = action.payload?.message || "Payment released";
      })
      .addCase(releasePayment.rejected, (state, action) => {
        state.loading.release = false;
        state.error = action.payload?.message || "Failed to release payment";
      })

      // ========== CANCEL PAYMENT (NEW) ==========
      .addCase(cancelPayment.pending, (state) => {
        state.loading.cancel = true;
        state.error = null;
      })
      .addCase(cancelPayment.fulfilled, (state, action) => {
        state.loading.cancel = false;
        const data = extractData(action.payload);
        const updatedPayment = data.payment || data;
        if (updatedPayment && updatedPayment._id) {
          // Update in main payments list
          const index = state.payments.findIndex(
            (p) => p._id === updatedPayment._id
          );
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
          // Update current payment if viewing it
          if (state.currentPayment?._id === updatedPayment._id) {
            state.currentPayment = updatedPayment;
          }
          // Remove from pending list (status changed to cancelled)
          state.pendingPayments = state.pendingPayments.filter(
            (p) => p._id !== updatedPayment._id
          );
          state.pendingCount = state.pendingPayments.length;
          state.pendingTotal = state.pendingPayments.reduce(
            (sum, p) => sum + (p.netAmount || 0),
            0
          );
        }
        state.successMessage = action.payload?.message || "Payment cancelled";
      })
      .addCase(cancelPayment.rejected, (state, action) => {
        state.loading.cancel = false;
        state.error = action.payload?.message || "Failed to cancel payment";
      })

      // ========== FETCH PAYMENTS BY KARIGAR ==========
      .addCase(fetchPaymentsByKarigar.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByKarigar.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = extractData(action.payload);
        state.payments = data.payments || data || [];
        if (data.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(fetchPaymentsByKarigar.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch payments";
        state.payments = [];
      })

      // ========== FETCH PAYMENTS BY JOB ==========
      .addCase(fetchPaymentsByJob.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByJob.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = extractData(action.payload);
        state.payments = data.payments || data || [];
      })
      .addCase(fetchPaymentsByJob.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch payments";
        state.payments = [];
      })

      // ========== FETCH PENDING PAYMENTS ==========
      .addCase(fetchPendingPayments.pending, (state) => {
        state.loading.pending = true;
        state.error = null;
      })
      .addCase(fetchPendingPayments.fulfilled, (state, action) => {
        state.loading.pending = false;
        const data = extractData(action.payload);
        state.pendingPayments = data.payments || data || [];
        state.pendingCount =
          data.pagination?.total || data.payments?.length || 0;
        state.pendingTotal = data.totalPendingAmount || 0; // ← Match backend response
        if (data.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(fetchPendingPayments.rejected, (state, action) => {
        state.loading.pending = false;
        state.error =
          action.payload?.message || "Failed to fetch pending payments";
        state.pendingPayments = [];
      })

      // ========== GENERATE PAYMENTS FOR PERIOD ==========
      .addCase(generatePaymentsForPeriod.pending, (state) => {
        state.loading.generate = true;
        state.error = null;
      })
      .addCase(generatePaymentsForPeriod.fulfilled, (state, action) => {
        state.loading.generate = false;
        const data = extractData(action.payload);
        // Add generated payments to list
        const generatedPayments = data.payments || [];
        if (generatedPayments.length > 0) {
          state.payments = [...generatedPayments, ...state.payments];
          // Also add to pending list
          state.pendingPayments = [
            ...generatedPayments,
            ...state.pendingPayments,
          ];
          state.pendingCount += generatedPayments.length;
          state.pendingTotal += generatedPayments.reduce(
            (sum, p) => sum + (p.netAmount || 0),
            0
          );
        }
        state.successMessage =
          action.payload?.message ||
          `${data.count || generatedPayments.length} payments generated`;
      })
      .addCase(generatePaymentsForPeriod.rejected, (state, action) => {
        state.loading.generate = false;
        state.error = action.payload?.message || "Failed to generate payments";
      })

      // ========== BULK PROCESS PAYMENTS ==========
      .addCase(bulkProcessPayments.pending, (state) => {
        state.loading.bulkProcess = true;
        state.error = null;
      })
      .addCase(bulkProcessPayments.fulfilled, (state, action) => {
        state.loading.bulkProcess = false;
        const data = extractData(action.payload);
        const results = data.results || {};
        const successPayments = results.success || [];
        const processedIds = successPayments.map((p) => p.paymentId);

        // Update status of processed payments in main list
        state.payments = state.payments.map((p) => {
          if (processedIds.includes(p._id)) {
            return { ...p, status: "completed" };
          }
          return p;
        });

        // Remove from pending
        state.pendingPayments = state.pendingPayments.filter(
          (p) => !processedIds.includes(p._id)
        );
        state.pendingCount = state.pendingPayments.length;
        state.pendingTotal = state.pendingPayments.reduce(
          (sum, p) => sum + (p.netAmount || 0),
          0
        );

        state.successMessage =
          action.payload?.message ||
          `${data.processedCount || processedIds.length} payments processed`;
      })
      .addCase(bulkProcessPayments.rejected, (state, action) => {
        state.loading.bulkProcess = false;
        state.error = action.payload?.message || "Failed to process payments";
      })

      // ========== FETCH PAYMENT STATS ==========
      .addCase(fetchPaymentStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchPaymentStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        const data = extractData(action.payload);
        state.stats = data;
      })
      .addCase(fetchPaymentStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload?.message || "Failed to fetch stats";
      })

      // ========== GENERATE PAYMENT REPORT ==========
      .addCase(generatePaymentReport.pending, (state) => {
        state.loading.report = true;
        state.error = null;
      })
      .addCase(generatePaymentReport.fulfilled, (state, action) => {
        state.loading.report = false;
        const data = extractData(action.payload);
        state.report = data.report || data;
      })
      .addCase(generatePaymentReport.rejected, (state, action) => {
        state.loading.report = false;
        state.error = action.payload?.message || "Failed to generate report";
      })

      // ========== FETCH KARIGAR EARNINGS ==========
      .addCase(fetchKarigarEarnings.pending, (state) => {
        state.loading.earnings = true;
        state.error = null;
      })
      .addCase(fetchKarigarEarnings.fulfilled, (state, action) => {
        state.loading.earnings = false;
        const data = extractData(action.payload);
        state.karigarEarnings = data.earnings || data;
      })
      .addCase(fetchKarigarEarnings.rejected, (state, action) => {
        state.loading.earnings = false;
        state.error = action.payload?.message || "Failed to fetch earnings";
      })

      // ========== CREATE ADVANCE PAYMENT ==========
      .addCase(createAdvancePayment.pending, (state) => {
        state.loading.advance = true;
        state.error = null;
      })
      .addCase(createAdvancePayment.fulfilled, (state, action) => {
        state.loading.advance = false;
        const data = extractData(action.payload);
        const newPayment = data.payment || data;
        if (newPayment && newPayment._id) {
          state.payments = [newPayment, ...state.payments];
          state.pagination.total += 1;
          // Add to pending
          if (newPayment.status === "pending") {
            state.pendingPayments = [newPayment, ...state.pendingPayments];
            state.pendingCount += 1;
            state.pendingTotal += newPayment.netAmount || 0;
          }
        }
        state.successMessage =
          action.payload?.message || "Advance payment created";
      })
      .addCase(createAdvancePayment.rejected, (state, action) => {
        state.loading.advance = false;
        state.error =
          action.payload?.message || "Failed to create advance payment";
      })

      // ========== CREATE DEDUCTION ==========
      .addCase(createDeduction.pending, (state) => {
        state.loading.deduction = true;
        state.error = null;
      })
      .addCase(createDeduction.fulfilled, (state, action) => {
        state.loading.deduction = false;
        const data = extractData(action.payload);
        const newPayment = data.payment || data;
        if (newPayment && newPayment._id) {
          state.payments = [newPayment, ...state.payments];
          state.pagination.total += 1;
          // Add to pending (deductions are also processed like payments)
          if (newPayment.status === "pending") {
            state.pendingPayments = [newPayment, ...state.pendingPayments];
            state.pendingCount += 1;
            // Note: deductions have negative netAmount
            state.pendingTotal += newPayment.netAmount || 0;
          }
        }
        state.successMessage = action.payload?.message || "Deduction created";
      })
      .addCase(createDeduction.rejected, (state, action) => {
        state.loading.deduction = false;
        state.error = action.payload?.message || "Failed to create deduction";
      })

      // ========== CALCULATE JOB PAYMENT ==========
      .addCase(calculateJobPayment.pending, (state) => {
        state.loading.calculate = true;
        state.error = null;
      })
      .addCase(calculateJobPayment.fulfilled, (state, action) => {
        state.loading.calculate = false;
        const data = extractData(action.payload);
        state.jobPaymentCalculation = data;
      })
      .addCase(calculateJobPayment.rejected, (state, action) => {
        state.loading.calculate = false;
        state.error = action.payload?.message || "Failed to calculate payment";
      })

      // ========== EXPORT PAYMENTS ==========
      .addCase(exportPayments.pending, (state) => {
        state.loading.export = true;
        state.error = null;
      })
      .addCase(exportPayments.fulfilled, (state, action) => {
        state.loading.export = false;
        state.successMessage = action.payload?.message || "Export completed";
      })
      .addCase(exportPayments.rejected, (state, action) => {
        state.loading.export = false;
        state.error = action.payload?.message || "Failed to export payments";
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  setFilters,
  resetFilters,
  clearCurrentPayment,
  clearReport,
  clearEarnings,
  clearJobPaymentCalculation,
  clearPendingPayments,
  setPagination,
  updatePaymentInList,
  removeFromPending,
} = paymentSlice.actions;

export default paymentSlice.reducer;
